export function TermsContent() {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <h1>SEEDFORGE, INC.</h1>
      <h2>TERMS AND CONDITIONS OF SERVICE</h2>
      <p>
        <strong>Effective Date: February 1, 2026</strong>
      </p>

      <p className="font-semibold uppercase">
        PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY. BY ACCESSING OR USING THE SEEDFORGE
        PLATFORM, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO ALL OF THESE TERMS,
        DO NOT ACCESS OR USE THE PLATFORM.
      </p>

      <p>
        These Terms and Conditions (&ldquo;Terms&rdquo;) constitute a legally binding agreement
        between you (&ldquo;User,&rdquo; &ldquo;you,&rdquo; or &ldquo;your&rdquo;) and SeedForge,
        Inc., a Delaware corporation (&ldquo;SeedForge,&rdquo; &ldquo;Company,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), governing your access to and use
        of the SeedForge platform, including our website, applications, application programming
        interfaces, and all related services (collectively, the &ldquo;Platform&rdquo;).
      </p>

      <h3>1. DEFINITIONS</h3>
      <p>For purposes of these Terms, the following definitions apply:</p>
      <p>
        &ldquo;<strong>Content</strong>&rdquo; means all information, data, text, software, music,
        sound, photographs, graphics, video, messages, materials, pitch decks, interview recordings,
        audio files, evaluation reports, company metrics, and any other materials submitted to,
        posted on, or transmitted through the Platform.
      </p>
      <p>
        &ldquo;<strong>Data Room</strong>&rdquo; means the verified startup profile and associated
        materials made available to Investors through the Platform for verified Founders.
      </p>
      <p>
        &ldquo;<strong>Evaluation Materials</strong>&rdquo; means pitch decks, video and audio
        interview recordings, company traction metrics, founder information, team member profiles,
        and any other data or materials submitted by Founders for evaluation.
      </p>
      <p>
        &ldquo;<strong>Founder</strong>&rdquo; means any User who registers on the Platform for the
        purpose of submitting a startup or business for evaluation.
      </p>
      <p>
        &ldquo;<strong>Investor</strong>&rdquo; means any User who registers on the Platform as a
        venture capital investor, angel investor, or other professional investor for the purpose of
        accessing verified startup information and Data Rooms.
      </p>
      <p>
        &ldquo;<strong>AI Systems</strong>&rdquo; means the artificial intelligence, machine
        learning, and automated evaluation systems utilized by SeedForge to analyze, evaluate, and
        process Content, including but not limited to avatar interview systems, scoring algorithms,
        and evaluation engines.
      </p>
      <p>
        &ldquo;<strong>Verified Status</strong>&rdquo; means the designation granted to Founders who
        successfully complete SeedForge&rsquo;s evaluation process and meet the Platform&rsquo;s
        verification criteria.
      </p>
      <p>
        &ldquo;<strong>SeedForge Score</strong>&rdquo; means the quantitative assessment generated
        by SeedForge&rsquo;s AI Systems based on evaluation of a Founder&rsquo;s Evaluation
        Materials.
      </p>

      <h3>2. ELIGIBILITY AND ACCOUNT REGISTRATION</h3>

      <h4>2.1 Age Requirement</h4>
      <p>
        You must be at least eighteen (18) years of age to use the Platform. By using the Platform,
        you represent and warrant that you are at least 18 years old and have the legal capacity to
        enter into these Terms.
      </p>

      <h4>2.2 Account Registration</h4>
      <p>
        To access certain features of the Platform, you must register for an account. When
        registering, you agree to:
      </p>
      <ul>
        <li>Provide accurate, current, and complete information during registration;</li>
        <li>
          Maintain and promptly update your account information to keep it accurate, current, and
          complete;
        </li>
        <li>Maintain the security and confidentiality of your login credentials;</li>
        <li>Accept responsibility for all activities that occur under your account; and</li>
        <li>Notify SeedForge immediately of any unauthorized use of your account.</li>
      </ul>

      <h4>2.3 Investor Verification</h4>
      <p>
        Users seeking to register as Investors must provide verification of their investor status.
        SeedForge verifies Investors through email domain verification and LinkedIn profile review.
        SeedForge reserves the right to request additional verification documentation and to deny or
        revoke Investor access to any User who cannot demonstrate legitimate investor status.
        Investor accounts are intended solely for professional investors, including venture capital
        investors, angel investors, family offices, and institutional investors.
      </p>

      <h4>2.4 Waitlist Registration</h4>
      <p>
        Users may register for our waitlist by providing their email address. By joining the
        waitlist, you consent to receive occasional product updates, announcements, and marketing
        communications from SeedForge. You may unsubscribe from these communications at any time
        using the unsubscribe link provided in each email or by contacting us directly.
      </p>

      <h3>3. PLATFORM SERVICES</h3>

      <h4>3.1 Service Description</h4>
      <p>
        SeedForge operates an AI-powered verification and evaluation platform that connects Founders
        with Investors. Our services include:
      </p>
      <ul>
        <li>Automated pre-screening and evaluation of startup submissions;</li>
        <li>Live AI-powered avatar interviews with Founders;</li>
        <li>Generation of SeedForge Scores and evaluation reports;</li>
        <li>Verification credentials for qualifying Founders;</li>
        <li>Data Rooms for verified Founders to share with Investors;</li>
        <li>Verified dealflow access for registered Investors; and</li>
        <li>Investor inbound screening and thesis-matching services.</li>
      </ul>

      <h4>3.2 Data Room Access</h4>
      <p>
        Access to Data Rooms is restricted to verified Investors and is provided on a view-only
        basis. Investors may not download, copy, reproduce, distribute, or otherwise extract
        materials from Data Rooms except as expressly permitted by SeedForge or the applicable
        Founder. By accessing a Data Room, Investors agree to maintain the confidentiality of all
        information contained therein.
      </p>

      <h4>3.3 Engagement Data</h4>
      <p>
        SeedForge may collect and share engagement analytics with Founders, including information
        about which Investors have viewed their Data Room, the duration of views, and other
        interaction metrics. By accessing a Data Room, Investors acknowledge and consent to this
        data collection and sharing.
      </p>

      <h3>4. FEES AND PAYMENT</h3>

      <h4>4.1 Pricing</h4>
      <p>
        Certain features of the Platform may be offered free of charge, while others may require
        payment. SeedForge reserves the right to introduce, modify, or discontinue any pricing at
        any time, with reasonable notice to affected Users. Current pricing information is available
        on our website.
      </p>

      <h4>4.2 Future Subscription Services</h4>
      <p>
        SeedForge may introduce subscription-based premium services for Investors. Any such services
        will be subject to additional terms disclosed at the time of purchase.
      </p>

      <h4>4.3 Digital Content Refund Policy</h4>
      <p>
        Fees paid for SeedForge Prep and Evaluation Reports are for the immediate delivery of
        digital content. You hereby expressly acknowledge that performance of the service begins
        immediately upon payment and you waive your right of withdrawal. All fees are non-refundable
        once the evaluation process has commenced, regardless of the resulting Score or feedback.
      </p>

      <h3>5. USER CONTENT AND LICENSE GRANTS</h3>

      <h4>5.1 Ownership</h4>
      <p>
        You retain ownership of the Content you submit to the Platform, subject to the license
        grants described herein. SeedForge does not claim ownership of your Content.
      </p>

      <h4>5.2 License to SeedForge</h4>
      <p>
        By submitting Content to the Platform, you grant SeedForge a worldwide, non-exclusive,
        royalty-free, sublicensable, perpetual, and irrevocable license to use, copy, modify, adapt,
        process, analyze, create derivative works from, and display such Content for the following
        purposes:
      </p>
      <ul>
        <li>Operating, providing, and improving the Platform and its services;</li>
        <li>Conducting AI-powered evaluations and generating scores and reports;</li>
        <li>
          Training, developing, and improving SeedForge&rsquo;s AI Systems and machine learning
          models, including using Content to create training datasets, fine-tune models, and enhance
          evaluation accuracy;
        </li>
        <li>
          Creating aggregated, anonymized, or de-identified data for analytics and benchmarking;
        </li>
        <li>
          Displaying Content to Investors and other Users as part of the Platform&rsquo;s services;
          and
        </li>
        <li>Any other purpose consistent with the operation and improvement of the Platform.</li>
      </ul>

      <h4>5.3 AI Training Acknowledgment</h4>
      <p className="font-semibold uppercase">
        YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT SEEDFORGE MAY USE YOUR CONTENT, INCLUDING
        EVALUATION MATERIALS, INTERVIEW RECORDINGS, AND ASSOCIATED METADATA, TO TRAIN AND IMPROVE
        ITS AI SYSTEMS.
      </p>
      <p>
        This may include, without limitation: using your pitch content to improve evaluation
        accuracy; analyzing interview responses to enhance AI interviewer capabilities;
        incorporating your data into training datasets that inform future model development; and
        creating derivative works or processed data that improve SeedForge&rsquo;s services.
      </p>
      <p>
        You explicitly consent to the capture, recording, storage, and processing of your image,
        voice, facial geometry, and behavioral identifiers (&ldquo;Biometric Data&rdquo;) during AI
        Avatar interviews. You grant SeedForge permission to use this data to generate behavioral
        analysis, verified credentials, and to train our AI Systems. You further acknowledge and
        agree that SeedForge personnel or authorized contractors may review, listen to, and watch
        your Content and Interview Recordings for the purposes of quality assurance, safety, bias
        detection, and model improvement. This license survives termination of your account, except
        where deletion rights are exercised as described in Section 8.
      </p>

      <h4>5.4 Content Representations</h4>
      <p>You represent and warrant that:</p>
      <ul>
        <li>You own or have the necessary rights to submit the Content;</li>
        <li>
          The Content does not infringe any third party&rsquo;s intellectual property, privacy, or
          other rights;
        </li>
        <li>The Content is accurate and not misleading; and</li>
        <li>
          You have obtained all necessary consents from individuals whose personal information
          appears in your Content.
        </li>
      </ul>

      <h4>5.5 Custom Audiences and Ad Targeting</h4>
      <p>
        You acknowledge and agree that SeedForge may use your hashed contact information (e.g.,
        email addresses) to generate &ldquo;Custom Audiences&rdquo; or &ldquo;Lookalike
        Audiences&rdquo; through third-party advertising platforms (such as LinkedIn, X, and Meta)
        for the purpose of targeting marketing campaigns to you or finding similar potential users.
        You may opt-out of this specific data usage by emailing{' '}
        <a href="mailto:legal@seedforge.com">legal@seedforge.com</a>.
      </p>

      <h3>6. PROHIBITED CONDUCT</h3>
      <p>You agree not to engage in any of the following prohibited activities:</p>

      <h4>6.1 General Prohibitions</h4>
      <ul>
        <li>Violating any applicable law, regulation, or these Terms;</li>
        <li>Submitting false, misleading, or fraudulent information;</li>
        <li>Impersonating any person or entity or misrepresenting your affiliation;</li>
        <li>Interfering with or disrupting the Platform or its servers;</li>
        <li>Attempting to gain unauthorized access to any portion of the Platform.</li>
      </ul>
      <p>
        You agree not to attempt to manipulate the AI Systems through &ldquo;prompt
        injection,&rdquo; logical paradoxes, or hidden text instructions designed to override
        evaluation parameters.
      </p>

      <h4>6.2 Scraping and Data Extraction</h4>
      <p>Users are strictly prohibited from:</p>
      <ul>
        <li>
          Using any automated means, including robots, spiders, scrapers, or data mining tools, to
          access, scrape, or extract data from the Platform;
        </li>
        <li>Downloading, copying, or extracting bulk data from the Platform;</li>
        <li>Building or populating a database using Platform content; and</li>
        <li>Circumventing any technological measures designed to protect Platform content.</li>
      </ul>

      <h4>6.3 Investor-Specific Prohibitions</h4>
      <p>Investors additionally agree not to:</p>
      <ul>
        <li>Register for an Investor account without being a bona fide investor;</li>
        <li>Share Investor account access credentials with non-investors;</li>
        <li>
          Permit any non-investor to access Founder Data Rooms or other investor-only content
          through their account;
        </li>
        <li>
          Use Founder information for competitive intelligence purposes unrelated to potential
          investment;
        </li>
        <li>
          Share, distribute, or disclose Founder information to third parties without Founder
          consent; and
        </li>
        <li>
          Use the Platform for any purpose other than evaluating potential investment opportunities.
        </li>
      </ul>

      <h3>7. THIRD-PARTY SERVICES AND DATA PROCESSING</h3>

      <h4>7.1 Third-Party Service Providers</h4>
      <p>
        SeedForge utilizes third-party service providers to deliver certain Platform features. By
        using the Platform, you acknowledge that your Content may be processed by the following
        third-party providers:
      </p>
      <ul>
        <li>OpenAI, Inc. (AI language model services)</li>
        <li>xAI Corp. (AI model services &ndash; Grok)</li>
        <li>Anthropic, PBC (AI model services &ndash; Claude)</li>
        <li>Google LLC (AI model services &ndash; Gemini)</li>
        <li>ElevenLabs, Inc. (AI voice synthesis services)</li>
        <li>Tavus, Inc. (AI avatar and video services)</li>
      </ul>

      <h4>7.2 Consent to Third-Party Processing</h4>
      <p>
        By using the Platform, you expressly consent to the processing of your Content by these
        third-party providers as necessary to provide the Platform&rsquo;s services. Each
        third-party provider operates under its own terms of service and privacy policy. SeedForge
        maintains appropriate data processing agreements with these providers to protect your
        information.
      </p>

      <h4>7.3 Financial Data Liability</h4>
      <p>
        By connecting financial accounts, you grant SeedForge and its service providers the right to
        access, transmit, and analyze your financial data. We use Stripe Financial Connections to
        access this data. By using this feature, you agree to{' '}
        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
          Stripe&rsquo;s End User Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="https://stripe.com/legal/financial-connections-end-user"
          target="_blank"
          rel="noopener noreferrer"
        >
          Stripe&rsquo;s Financial Connections Terms
        </a>
        .
      </p>
      <p>
        You acknowledge that SeedForge does not verify the accuracy of data provided by third-party
        APIs and is not liable for any Evaluation Errors or &ldquo;SeedForge Scores&rdquo; resulting
        from data synchronization failures.
      </p>

      <h4>7.4 Partner Inbound</h4>
      <p>
        If you access the Platform via a Partner Link (e.g., an Investor&rsquo;s dedicated
        submission page), you acknowledge that you are registering an account with SeedForge
        directly. Your Content will be subject to these Terms, including SeedForge&rsquo;s right to
        use such Content for AI training, regardless of whether the specific Investor chooses to
        invest.
      </p>

      <h3>8. DATA RIGHTS AND DELETION</h3>

      <h4>8.1 Founder Data Control</h4>
      <p>
        Founders may request to disable and delete their Content and Evaluation Materials from
        active use on the Platform. Upon receiving a valid deletion request, SeedForge will:
      </p>
      <ul>
        <li>Remove the Founder&rsquo;s profile and Data Room from public visibility;</li>
        <li>
          Delete or de-identify the Founder&rsquo;s Content from active databases within thirty (30)
          days; and
        </li>
        <li>Revoke any Verified Status and associated credentials.</li>
      </ul>

      <h4>8.2 Limitations on Deletion</h4>
      <p>You acknowledge that:</p>
      <ul>
        <li>
          Aggregated, anonymized, or de-identified data derived from your Content may be retained;
        </li>
        <li>Data incorporated into trained AI models cannot be selectively removed;</li>
        <li>
          SeedForge may retain certain data as required by law or for legitimate business purposes;
          and
        </li>
        <li>Backup systems may retain copies for a reasonable period following deletion.</li>
      </ul>

      <h4>8.3 Data Retention</h4>
      <p>
        SeedForge retains non-biometric User data indefinitely to support ongoing platform
        operations and historical analysis. Notwithstanding the foregoing, Biometric Data (including
        facial geometry and voiceprints) shall be permanently destroyed on the earlier of: (i) the
        date the initial purpose for collecting such data has been satisfied; or (ii) three (3)
        years after your last interaction with the Platform.
      </p>

      <h3>9. DISCLAIMER OF INVESTMENT ADVICE</h3>
      <p className="font-semibold uppercase">
        THE PLATFORM, INCLUDING ALL SEEDFORGE SCORES, EVALUATION REPORTS, AND OTHER OUTPUTS, IS
        PROVIDED FOR INFORMATIONAL PURPOSES ONLY AND DOES NOT CONSTITUTE INVESTMENT ADVICE,
        FINANCIAL ADVICE, LEGAL ADVICE, OR ANY OTHER PROFESSIONAL ADVICE.
      </p>

      <h4>9.1 No Investment Recommendation</h4>
      <p>
        SeedForge does not recommend or endorse any particular investment opportunity. SeedForge
        Scores and evaluation reports are algorithmic outputs generated by AI Systems and are not
        recommendations to invest or not invest in any particular startup. Investment decisions
        involve substantial risk and should be made only after conducting your own independent due
        diligence and, where appropriate, consulting with qualified financial, legal, and tax
        advisors.
      </p>

      <h4>9.2 Not a Broker-Dealer</h4>
      <p>
        SeedForge is not a registered broker-dealer, investment adviser, or securities exchange. The
        Platform does not facilitate securities transactions or provide personalized investment
        recommendations. SeedForge does not receive transaction-based compensation for connecting
        Founders with Investors.
      </p>

      <h4>9.3 No Guarantee of Accuracy</h4>
      <p>
        While SeedForge endeavors to provide accurate and useful evaluations, we do not guarantee
        the accuracy, completeness, reliability, or usefulness of any evaluation, score, or report.
        Founder-provided information is not independently verified except as explicitly stated. Past
        performance, as indicated by any metric or score, is not indicative of future results.
      </p>

      <h4>9.4 AI Persona and Output Style</h4>
      <p>
        You acknowledge that the AI Systems, including the Avatar Interviewer, may simulate human
        personalities, tones, or communication styles that vary. SeedForge is not responsible for
        any perceived offense, insult, or emotional distress caused by the tone, style, or specific
        word choices of the AI Systems. The AI&rsquo;s &ldquo;personality&rdquo; is generated
        dynamically and does not reflect the official views of SeedForge, Inc.
      </p>

      <h3>10. INTELLECTUAL PROPERTY</h3>

      <h4>10.1 SeedForge Intellectual Property</h4>
      <p>
        The Platform, including its software, algorithms, AI Systems, design, features,
        functionality, text, graphics, logos, and trademarks, is owned by SeedForge and protected by
        intellectual property laws. Nothing in these Terms grants you any right, title, or interest
        in SeedForge&rsquo;s intellectual property except the limited right to use the Platform in
        accordance with these Terms.
      </p>

      <h4>10.2 Feedback</h4>
      <p>
        If you provide SeedForge with any feedback, suggestions, or ideas regarding the Platform
        (&ldquo;Feedback&rdquo;), you grant SeedForge an unrestricted, perpetual, irrevocable,
        royalty-free license to use such Feedback for any purpose without compensation or
        attribution to you.
      </p>

      <h4>10.3 Publicity Grant</h4>
      <p>
        Notwithstanding the confidentiality provisions herein, you agree that SeedForge may identify
        you as a User of the Platform on our website, pitch decks, and marketing materials using
        your name, company name, and logo. We will not publish your confidential Evaluation
        Materials (e.g., your full Pitch Deck) publicly without your specific consent.
      </p>

      <h3>11. INDEMNIFICATION</h3>

      <h4>11.1 General Indemnification</h4>
      <p>
        You agree to indemnify, defend, and hold harmless SeedForge, its officers, directors,
        employees, agents, licensors, and service providers from and against any and all claims,
        damages, losses, costs, and expenses (including reasonable attorneys&rsquo; fees) arising
        out of or relating to:
      </p>
      <ul>
        <li>Your use of the Platform;</li>
        <li>Your Content;</li>
        <li>Your violation of these Terms;</li>
        <li>Your violation of any applicable law or third-party right; and</li>
        <li>Any misrepresentation made by you.</li>
      </ul>

      <h4>11.2 Investor-Specific Indemnification</h4>
      <p>
        Investors additionally agree to indemnify SeedForge from and against claims arising from:
      </p>
      <ul>
        <li>Misuse of Founder information obtained through the Platform;</li>
        <li>Unauthorized sharing or disclosure of Data Room contents;</li>
        <li>Misrepresentation of investor status;</li>
        <li>Permitting non-investors to access investor-only features;</li>
        <li>Any investment decisions made based on Platform information; and</li>
        <li>Claims brought by Founders arising from Investor&rsquo;s use of their information.</li>
      </ul>

      <h3>12. LIMITATION OF LIABILITY</h3>

      <h4>12.1 Disclaimer of Warranties</h4>
      <p className="font-semibold uppercase">
        THE PLATFORM IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT
        WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. SEEDFORGE
        DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
      </p>

      <h4>12.2 Limitation of Damages</h4>
      <p className="font-semibold uppercase">
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SEEDFORGE BE LIABLE FOR
        ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
        LIMITED TO LOSS OF PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, REGARDLESS OF THE
        CAUSE OF ACTION OR WHETHER SEEDFORGE HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>

      <h4>12.3 Cap on Liability</h4>
      <p className="font-semibold uppercase">
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SEEDFORGE&rsquo;S TOTAL CUMULATIVE
        LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE PLATFORM
        SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU HAVE PAID TO SEEDFORGE IN THE TWELVE
        (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100).
      </p>

      <h3>13. DISPUTE RESOLUTION</h3>

      <h4>13.1 Informal Resolution</h4>
      <p>
        Before initiating any formal legal proceeding, you agree to first contact SeedForge and
        attempt to resolve any dispute informally. You must send a written notice describing the
        dispute to <a href="mailto:legal@seedforge.com">legal@seedforge.com</a> (or such other
        address as SeedForge may designate). The parties shall engage in good faith negotiations for
        a period of thirty (30) days following receipt of such notice. Most disputes can be resolved
        quickly and amicably through direct communication.
      </p>

      <h4>13.2 Governing Law</h4>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the State of
        Delaware, United States, without regard to its conflict of law principles.
      </p>

      <h4>13.3 Exclusive Jurisdiction</h4>
      <p>
        If informal resolution is unsuccessful, any legal action or proceeding arising out of or
        relating to these Terms shall be brought exclusively in the state or federal courts located
        in the State of Delaware. You consent to the personal jurisdiction and venue of such courts
        and waive any objection to inconvenient forum.
      </p>

      <h4>13.4 Class Action Waiver</h4>
      <p className="font-semibold uppercase">
        YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL
        BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.
      </p>

      <h3>14. TERMINATION</h3>

      <h4>14.1 Termination by You</h4>
      <p>
        You may terminate your account at any time by contacting SeedForge or using the account
        deletion feature in the Platform settings, where available.
      </p>

      <h4>14.2 Termination by SeedForge</h4>
      <p>
        SeedForge may suspend or terminate your account and access to the Platform at any time, with
        or without cause, and with or without notice. Reasons for termination may include, but are
        not limited to, violation of these Terms, fraudulent or illegal activity, or at
        SeedForge&rsquo;s sole discretion.
      </p>

      <h4>14.3 Effect of Termination</h4>
      <p>
        Upon termination, your right to access and use the Platform will immediately cease. Sections
        of these Terms that by their nature should survive termination shall survive, including but
        not limited to Sections 5 (User Content and License Grants), 9 (Disclaimer of Investment
        Advice), 11 (Indemnification), 12 (Limitation of Liability), 13 (Dispute Resolution), and 16
        (General Provisions).
      </p>

      <h3>15. PRIVACY AND DATA PROTECTION</h3>

      <h4>15.1 Privacy Policy</h4>
      <p>
        Your use of the Platform is also governed by our Privacy Policy, which is incorporated into
        these Terms by reference. Please review our Privacy Policy to understand our practices
        regarding the collection, use, and disclosure of your personal information.
      </p>

      <h4>15.2 Data Location</h4>
      <p>
        SeedForge&rsquo;s servers are located in the European Union. By using the Platform, you
        consent to the transfer and processing of your data in the EU and, as necessary, to the
        United States and other jurisdictions where our service providers operate.
      </p>

      <h4>15.3 GDPR Compliance</h4>
      <p>
        For Users located in the European Economic Area (EEA), United Kingdom, or Switzerland,
        SeedForge processes personal data in compliance with the General Data Protection Regulation
        (GDPR) and applicable data protection laws. Your rights under GDPR, including rights of
        access, rectification, erasure, and data portability, are detailed in our Privacy Policy.
      </p>

      <h3>16. GENERAL PROVISIONS</h3>

      <h4>16.1 Modifications to Terms</h4>
      <p>
        SeedForge reserves the right to modify these Terms at any time. We will provide notice of
        material changes by posting the updated Terms on the Platform and updating the
        &ldquo;Effective Date&rdquo; above. Your continued use of the Platform following the posting
        of changes constitutes your acceptance of such changes. If you do not agree to the modified
        Terms, you must stop using the Platform.
      </p>

      <h4>16.2 Entire Agreement</h4>
      <p>
        These Terms, together with the Privacy Policy and any additional terms you agree to when
        using specific Platform features, constitute the entire agreement between you and SeedForge
        regarding your use of the Platform and supersede all prior agreements and understandings.
      </p>

      <h4>16.3 Severability</h4>
      <p>
        If any provision of these Terms is held to be invalid, illegal, or unenforceable, such
        provision shall be modified to the minimum extent necessary to make it valid, legal, and
        enforceable. If such modification is not possible, the provision shall be severed, and the
        remaining provisions shall continue in full force and effect.
      </p>

      <h4>16.4 Waiver</h4>
      <p>
        SeedForge&rsquo;s failure to enforce any right or provision of these Terms shall not
        constitute a waiver of such right or provision. Any waiver must be in writing and signed by
        an authorized representative of SeedForge.
      </p>

      <h4>16.5 Assignment</h4>
      <p>
        You may not assign or transfer these Terms or your rights hereunder without
        SeedForge&rsquo;s prior written consent. SeedForge may assign these Terms without
        restriction.
      </p>

      <h4>16.6 Notices</h4>
      <p>
        SeedForge may provide notices to you via email, posting on the Platform, or other reasonable
        means. You may provide notices to SeedForge by email at{' '}
        <a href="mailto:legal@seedforge.com">legal@seedforge.com</a> or by mail to: SeedForge, Inc.,
        1111b South Governors Ave, STE 43130, Dover, DE 19904, United States.
      </p>

      <h4>16.7 Force Majeure</h4>
      <p>
        SeedForge shall not be liable for any failure or delay in performing its obligations under
        these Terms due to causes beyond its reasonable control, including but not limited to acts
        of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or military
        authorities, fire, floods, accidents, strikes, or shortages of transportation, facilities,
        fuel, energy, labor, or materials.
      </p>

      <h3>17. CONTACT INFORMATION</h3>
      <p>If you have any questions about these Terms, please contact us at:</p>
      <address className="not-italic">
        <strong>SeedForge, Inc.</strong>
        <br />
        1111b South Governors Ave, STE 43130
        <br />
        Dover, DE 19904
        <br />
        United States
        <br />
        Email: <a href="mailto:legal@seedforge.com">legal@seedforge.com</a>
      </address>

      <hr />

      <h3>ACCEPTANCE OF TERMS</h3>
      <p>
        By clicking &ldquo;I Accept,&rdquo; &ldquo;I Agree,&rdquo; or similar button, by checking a
        box indicating acceptance, or by accessing or using the Platform, you acknowledge that you
        have read, understood, and agree to be bound by these Terms and Conditions.
      </p>
      <p className="font-semibold uppercase">
        IF YOU DO NOT AGREE TO THESE TERMS, DO NOT ACCESS OR USE THE PLATFORM.
      </p>

      <p className="text-muted-foreground">Last Updated: February 1, 2026</p>
      <p className="text-muted-foreground">&copy; 2026 SeedForge, Inc. All rights reserved.</p>
    </div>
  )
}
